import React, { useEffect, useMemo, useState } from "react";import { useTranslation } from "react-i18next";
import { calculate, getClient, listIngestRuns } from "../lib/api";
import { errorMessage } from "../lib/errorMessage";
import { buildCalcPayload } from "../lib/calcPayload";
import { useAuth } from "../auth/AuthContext";
import { useWorkflowOptional } from "../workflow/WorkflowContext";
import { detectFilingRange } from "../lib/filingPeriods";
import { CalcJobSurfaces } from "./calculate/CalcJobSurfaces";
import { RunActionsConfirmDialog } from "./calculate/RunActionsConfirmDialog";
import { BlockingMessageDialog } from "./BlockingMessageDialog";
import { useAsyncCalc } from "./useAsyncCalc";
import { useMirrorUp } from "./calculate/useMirrorUp";
import { sameSet } from "../workflow/calcInputs";
import { RegistrationsWarning } from "./RegistrationsWarning";
import { RunRow } from "./RunRow";
import { makeConflictPredicate } from "./runSelectionConflict";
import { SelectedRunsBanner } from "./SelectedRunsBanner";
import { UnregisteredDepartureBanner } from "./UnregisteredDepartureBanner";
import { aggregateUnregisteredCountries, aggregateUnregisteredDetail } from "../lib/unregisteredDepartures";
import { ClientSelector } from "./ClientSelector";
import { FilingPeriodCard } from "./FilingPeriodCard";
import { FxRatesSection } from "./calculate/FxRatesSection";
import { deriveFlaggedFxPairs, deriveFlaggedMonthlyFxPairs, computeFxMissing, monthlyFxInvalidCells, fxInvalidText } from "./calculate/flaggedFx";
import { useFxPreview } from "./calculate/useFxPreview";
import { monthlyFxPairs, monthsBetween, reachableMonthlyFx } from "./calculate/fxNeeds";
import { ProductionRunsList } from "./calculate/ProductionRunsList";
import { useUploadSearch } from "./calculate/useUploadSearch";
import { useDuplicateCollapse } from "./calculate/useDuplicateCollapse";
import { UploadNextStep } from "./calculate/UploadNextStep";
import { BlockCard } from "./BlockCard";
import { FxNote } from "./calculate/FxRatesSection";
import { useRunActions } from "./calculate/useRunActions";
import { useAutoDetectedPeriodSync } from "./calculate/useAutoDetectedPeriodSync";
import { periodDayCount, MAX_CALC_PERIOD_DAYS } from "../lib/clientFrequency";
import { Calculator, ArrowRight, FileArrowUp, CurrencyEur } from "@phosphor-icons/react";

// Digital-Services scope: the marketplace/fixture classifier was
// pruned along with the marketplace adapters. No uploaded run is a
// "fixture" in this project, so isFixture is a constant false.
const isFixture = () => false;

/**
 * U8/U9/U10 — Calculate panel:
 *   - U8: seller country pulled from Settings → company VAT registrations
 *   - U9: regime dropdown removed; engine auto-routes per transaction
 *   - U10: production / fixture distinction + selected-runs chips
 */
// A1-93 (5) — the panel holds local copies of the inputs (seeded from the
// workflow once, at mount). Keyed on the workflow's panelKey so Start over,
// sign-out and opening a calc rebuild it from context; on step 1 it used to
// survive Start over with its files still ticked.
export const CalculatePanel = (props) => {
  const wf = useWorkflowOptional();
  return <CalculatePanelBody key={wf?.panelKey || 0} {...props} />;
};

const CalculatePanelBody = ({ onCalculated, refreshKey, uploadSlot = null, justUploadedId = null }) => {
  const { t } = useTranslation();
  const { admin } = useAuth();
  // Pull from WorkflowContext when available; useWorkflowOptional is
  // safe to call without a provider (rules-of-hooks compliant).
  const wf = useWorkflowOptional();
  const [runs, setRunsLocal] = useState([]);
  // v0.4.24 — when a WorkflowProvider is mounted (normal Dashboard
  // path), the source of truth for `runs` is the context (gets
  // refreshed by UploadPanel.onIngested). Fall back to the local
  // state only when rendered without a provider.
  const ctxRuns = wf?.runs;
  const effectiveRuns = ctxRuns !== undefined ? ctxRuns : runs;
  const setRuns = setRunsLocal;
  const [selected, setSelected] = useState(() => wf?.selectedRunIds || []);
  const [periodStart, setPeriodStart] = useState(() => wf?.periodStart || "2025-01-01");
  const [periodEnd, setPeriodEnd] = useState(() => wf?.periodEnd || "2025-12-31");
  const [registrations, setRegistrations] = useState(() => wf?.registrations || []);
  const [clientFreqData, setClientFreqData] = useState(null); // v0.6.8
  const [settingsError, setSettingsError] = useState(null);
  const [regsReadFor, setRegsReadFor] = useState(null);   // the client whose registrations are loaded
  const [busy, setBusy] = useState(false);
  const [showBlockedDialog, setShowBlockedDialog] = useState(false);
  const [error, setError] = useState(null);
  // v0.5.0g+2 — "hide duplicate filenames" derivation retained (no
  // longer operator-toggleable after the bulk-actions toolbar was
  // removed in the Digital-Services prune; defaults to showing all).
  const hideDupes = false;
  const [countryFilter, setCountryFilter] = useState(() => wf?.countryFilter || "ALL");
  // v0.6 — FX rate maps read/written DIRECTLY from WorkflowContext (single
  // source of truth, no local mirror — a local copy could survive "Start
  // Over" and get submitted stale, the cause of the old 422 on this field).
  // v0.4.21 S5 — operator-declared period FX rates. {EUR: "0.8650"}
  const periodFxRates = wf?.periodFxRates || {};
  const setPeriodFxRates = wf?.setPeriodFxRates || (() => {});
  // v0.5.9m — per-month national FX. {SRC:{TGT:{"YYYY-MM":{rate,source}}}}
  const monthlyFxRates = wf?.monthlyFxRates || {};
  const setMonthlyFxRates = wf?.setMonthlyFxRates || (() => {});
  // v0.4.34 P2 — client selected at Step 1; mid-calc switching blocked (D3=a).
  const [clientId, setClientId] = useState(() => wf?.clientId || null);

  // Mirror local CHANGES up to the workflow context so the stepper sees
  // truth (FX maps are not mirrored — they ARE the context value). Never on
  // mount: see ./calculate/useMirrorUp.js (A1-93 round 4).
  useMirrorUp(selected, wf?.setSelectedRunIds);
  useMirrorUp(periodStart, wf?.setPeriodStart);
  useMirrorUp(periodEnd, wf?.setPeriodEnd);
  useMirrorUp(countryFilter, wf?.setCountryFilter);

  // v0.5.9m's "seed FX from the calc into an EMPTY grid" effect lived
  // here. A1-93 (1): when a different calc was opened the grid was not
  // empty, so it kept the previous calc's rates and "Save & re-run" sent
  // them. WorkflowContext.openCalc() now puts the opened calc's own FX on
  // the form, every time.

  useEffect(() => {
    listIngestRuns().then(setRuns).catch(() => {});
  }, [refreshKey]);

  useEffect(() => {
    // v0.4.37 P0 fix (2026-06-04): SELLER VAT REGISTRATIONS read from the
    // SELECTED CLIENT PROFILE, not global settings — without this guard a
    // client switch left stale data from the prior client on screen.
    if (!clientId) {
      setRegistrations([]); setClientFreqData(null); setSettingsError(null);
      return;
    }
    getClient(clientId)
      .then((c) => {
        const regs = (c.company_vat_numbers || [])
          .map((e) => (e.country || e.country_code || "").toUpperCase())
          .filter(Boolean);
        setRegistrations(Array.from(new Set(regs))); setSettingsError(null); setRegsReadFor(clientId);
        const { company_vat_numbers, uk_vat_frequency, local_return_frequency, uk_stagger_group,
          uk_annual_year_end_month } = c;   // roadmap 5b.4: UK quarters follow the stagger group
        setClientFreqData({ company_vat_numbers, uk_vat_frequency, local_return_frequency, uk_stagger_group,
          uk_annual_year_end_month });
      })
      .catch((e) => {
        setSettingsError(e?.response?.status === 401
          ? t("ui.calculate.settings_signin_hint") : t("ui.calculate.settings_load_failed"));
      });
  }, [clientId, t]);

  const toggle = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const selectedRuns = useMemo(
    () => effectiveRuns.filter((r) => selected.includes(r.id)),
    [effectiveRuns, selected]
  );

  // v0.4.37 CL3A: the filing period auto-detected from the selected runs,
  // rounded to the client's whole tax period (lib/filingPeriods).
  const autoDetectedRange = useMemo(
    () => detectFilingRange(selectedRuns, clientFreqData), [selectedRuns, clientFreqData]);

  // A1-93 — the open calc's period is kept only while ITS files are the
  // selection; once the files change, the period follows them again (else
  // August files were calculated for July). Read from the local selection,
  // which changes in the same render as the detected range.
  const keepCalcPeriod = (!!wf?.calc && sameSet(wf.calc.run_ids, selected))
    ? { start: String(wf.calc.period_start || "").slice(0, 10), end: String(wf.calc.period_end || "").slice(0, 10) } : null;
  useAutoDetectedPeriodSync(autoDetectedRange, periodStart, periodEnd, setPeriodStart, setPeriodEnd, keepCalcPeriod, wf?.autoPeriodRef);

  const prodRuns = useMemo(
    () => {
      // Newest first, one list (v0.5.0i+10). Operator 2026-10-17 (A2-396):
      // only the selected client's uploads — another client's file was listed,
      // could be ticked, and was then silently dropped. Uploads without a
      // client belong to the default client; no client picked shows all.
      const defaultId = (wf?.clients || []).find((c) => c.is_default)?.id;
      const isDefault = clientId === "default" || clientId === defaultId;
      const mine = (r) => !clientId || (r.client_id && r.client_id !== "default"
        ? r.client_id === clientId : isDefault);
      return effectiveRuns
        .filter(mine)
        .sort((a, b) => {
          const ad = a.created_at || "";
          const bd = b.created_at || "";
          return bd.localeCompare(ad);
        });
    },
    [effectiveRuns, clientId, wf?.clients],
  );
  const { uploadQuery, setUploadQuery, searchedProdRuns } = useUploadSearch(prodRuns);

  const { displayedProdRuns, earlierCountByRow, duplicateCount } = useDuplicateCollapse(searchedProdRuns, hideDupes);

  // v0.5.0i+10 — `fixtureRuns` useMemo + `<TestFixturesSection>`
  // render block removed. Operators have full visibility of every
  // upload in the single `prodRuns` list above.

  // v0.4.16 C1 — dynamic country list from selected runs.
  const countryOptions = useMemo(() => {
    const set = new Set();
    for (const r of selectedRuns) {
      for (const c of [...(r.departure_countries || []), ...(r.purchase_countries || [])]) {
        if (c) set.add(String(c).toUpperCase());
      }
    }
    return ["ALL", ...Array.from(set).sort()];
  }, [selectedRuns]);

  const noRegistrations = registrations.length === 0;
  const regime = registrations.includes("GB") ? "UK_VAT" : "EU_OSS";
  // v0.5.2 / v0.6 — see ./calculate/flaggedFx.js for what/why. Kept as
  // tiny pure functions there so this file stays under its LOC cap.
  const flaggedFxPairs = useMemo(
    () => deriveFlaggedFxPairs(wf?.review?.groups), [wf?.review],
  );
  const flaggedMonthlyFxPairs = useMemo(
    () => deriveFlaggedMonthlyFxPairs(wf?.review?.groups), [wf?.review],
  );

  // v0.6 Stage 2 — see ./calculate/useFxPreview.js for what/why. Kept as
  // its own hook there so this file stays under its LOC cap.
  const {
    fxPreviewLoading, mayBeIncomplete, previewAnswered,
    mergedFlaggedFxPairs, mergedFlaggedMonthlyFxPairs,
  } = useFxPreview({
    registrations, selected, periodStart, periodEnd, countryFilter,
    regime, clientId, noRegistrations,
    flaggedFxPairs, flaggedMonthlyFxPairs,
  });

  // v0.6 — see ./calculate/flaggedFx.js::computeFxMissing (two-target gate).
  const fxMissing = computeFxMissing(selectedRuns, regime, periodFxRates, mergedFlaggedFxPairs);
  const usableMonthlyFx = useMemo(() => reachableMonthlyFx(   // A2-474
    monthlyFxRates, monthlyFxPairs(selectedRuns, registrations, mergedFlaggedMonthlyFxPairs),
    monthsBetween(periodStart, periodEnd)),
  [monthlyFxRates, selectedRuns, registrations, mergedFlaggedMonthlyFxPairs, periodStart, periodEnd]);
  const fxInvalid = monthlyFxInvalidCells(usableMonthlyFx); // v0.6.10
  // Period validity: start ≤ end, both set, not implausibly long (v0.6.8 — mirrors filing_cadence_guard.py's hard bound).
  const periodDays = periodDayCount(periodStart, periodEnd);
  const periodTooLong = periodDays > MAX_CALC_PERIOD_DAYS;
  const periodValid = !!periodStart && !!periodEnd && periodStart <= periodEnd && !periodTooLong;
  const blockedReason = noRegistrations ? t("ui.calculate.blocked_no_registrations")
    : periodTooLong ? t("ui.calculate.blocked_period_too_long", { defaultValue: `Filing period is ${periodDays} days — longer than any real filing period. Check which runs are selected above.`, days: periodDays })
    : !periodValid ? t("ui.calculate.blocked_invalid_period", { defaultValue: "Set a valid filing period (start on or before end) first." })
    : selected.length === 0 ? t("ui.calculate.select_a_run")
    : fxMissing.length > 0 ? t("ui.calculate.blocked_fx_rates_missing", { missing: fxMissing.join(", ") })
    : fxInvalid.length > 0 ? fxInvalidText(t, fxInvalid)
    : null;

  // v0.4.34 P2/P3 — selected client governs the routing.
  // v0.4.39 T1 — async calc orchestration (hook keeps this file
  // under the LOC cap; see ./useAsyncCalc.js).
  const calcPoll = useAsyncCalc({ wf, onCalculated, setBusy, setError, t });

  const run = async () => {
    if (noRegistrations) {
      setError(t("ui.calculate.blocked_no_registrations"));
      return;
    }
    if (selected.length === 0) {
      setError(t("ui.calculate.select_a_run"));
      return;
    }
    if (!periodValid) {
      setError(t("ui.calculate.blocked_invalid_period", { defaultValue: "Set a valid filing period (start on or before end) first." }));
      return;
    }
    if (fxMissing.length > 0) {
      setError(t("ui.calculate.blocked_fx_rates_missing", { missing: fxMissing.join(", ") }));
      return;
    }
    if (fxInvalid.length > 0) { setError(fxInvalidText(t, fxInvalid)); return; }
    setBusy(true);
    setError(null);
    calcPoll.reset();
    try {
      // v0.6 — payload built via the shared buildCalcPayload() helper
      // (frontend/src/lib/calcPayload.js), also used by
      // WorkflowContext.jsx's runCalc(), instead of each call site
      // spelling out the same field list by hand.
      await calcPoll.start(buildCalcPayload({
        registrations, runIds: selected,
        periodStart, periodEnd, countryFilter,
        periodFxRates, monthlyFxRates: usableMonthlyFx, clientId,
      }));
    } catch (e) {
      setBusy(false);
      setError(errorMessage(e));
    }
  };

  const { bulkDeleting, onDeleteRun, doBulkDelete, onDownloadRun, confirmState, dismissConfirm }
    = useRunActions({ wf, setRuns, setSelected, setError });

  const onBulkDelete = () => doBulkDelete(
    [...selected],
    t("ui.calculate.bulk_delete_confirm", {
      count: selected.length,
      defaultValue: `Delete ${selected.length} selected upload(s)? Files locked by a signed/frozen calculation will be skipped. This cannot be undone.`,
    }),
  );

  const onDeleteAll = () => doBulkDelete(
    prodRuns.map((r) => r.id),
    t("ui.calculate.delete_all_confirm", {
      count: prodRuns.length,
      defaultValue: `Delete ALL ${prodRuns.length} uploads? Files locked by a signed/frozen calculation will be skipped. This cannot be undone.`,
    }),
  );

  // v0.4.37 V3 — DUPLICATE badge derived from current selection.
  const isConflict = makeConflictPredicate(runs, selected);

  const renderRunRow = (r) => (
    <RunRow key={r.id} run={r} isFixture={isFixture(r)}
      checked={selected.includes(r.id)} onToggle={toggle}
      onDelete={admin?.role === "ADMIN" ? onDeleteRun : undefined}
      onDownload={onDownloadRun}
      conflictsWithSelection={isConflict(r)}
      earlierCount={earlierCountByRow[r.id] || 0} justUploaded={r.id === justUploadedId}
    />
  );

  // v0.4.33 U2 — aggregate UNREGISTERED departure countries.
  // v0.6.12 A2-184 — reduction in lib/unregisteredDepartures.js (LOC cap).
  const aggregatedUnregistered = useMemo(() => aggregateUnregisteredCountries(selectedRuns), [selectedRuns]);
  const aggregatedUnregisteredDetail = useMemo(() => aggregateUnregisteredDetail(selectedRuns), [selectedRuns]);

  return (
    <section
      data-testid="calculate-panel"
      className="surface-section rounded-xl"
    >
      <div className="section-header">
        <div data-testid="calculate-section-title" className="section-header-title">
          {t("ui.calculate.section_title")}
        </div>
        <div className="section-header-note">
          {t("ui.calculate.section_subtitle")}
        </div>
      </div>

      <div className="space-y-5 p-4">{/* A2-467 — four numbered steps, one hue each */}
      <ClientSelector
        value={clientId}
        onChange={(id) => { setClientId(id); wf?.setClientId?.(id); }}
        disabled={busy || !!wf?.calc}
        isAdmin={admin?.role === "ADMIN"} step="A" accent="client"
      ><RegistrationsWarning registrations={registrations} settingsError={settingsError} clientLocked={!!wf?.calc}
        pending={!settingsError && regsReadFor !== clientId} /></ClientSelector>
      <BlockCard step="B" accent="files" icon={FileArrowUp} flush testid="calc-step-files"
        title={t("ui.calculate.files_block_title", "Which files to calculate")}
        purpose={uploadSlot ? t("ui.calculate.files_block_purpose", "Upload a file, then tick each file to include")
          : t("ui.calculate.files_block_purpose_no_upload", "Tick each file to include")}>
        {uploadSlot}
        <UploadNextStep run={prodRuns.find((r) => r.id === justUploadedId)}
          checked={selected.includes(justUploadedId)} onInclude={() => toggle(justUploadedId)} />
        <ProductionRunsList
          prodRuns={prodRuns}
          displayedProdRuns={displayedProdRuns}
          hideDupes={hideDupes}
          duplicateCount={duplicateCount}
          isAdmin={admin?.role === "ADMIN"}
          selectedCount={selected.length}
          bulkDeleting={bulkDeleting}
          onBulkDelete={onBulkDelete}
          onDeleteAll={onDeleteAll}
          renderRunRow={renderRunRow} query={uploadQuery} onQueryChange={setUploadQuery}
        />
        <UnregisteredDepartureBanner countries={aggregatedUnregistered} detail={aggregatedUnregisteredDetail} />
        {selectedRuns.length > 0 && <SelectedRunsBanner selectedRuns={selectedRuns} isFixture={isFixture} onToggle={toggle} />}
      </BlockCard>
      <FilingPeriodCard
        periodStart={periodStart} setPeriodStart={setPeriodStart}
        periodEnd={periodEnd} setPeriodEnd={setPeriodEnd}
        countryFilter={countryFilter} setCountryFilter={setCountryFilter}
        countryOptions={countryOptions}
        selectedRuns={selectedRuns} selected={selected}
        autoDetectedRange={autoDetectedRange}
        client={clientFreqData} step="C" accent="period"
      />
      <BlockCard step="D" accent="fx" icon={CurrencyEur} flush testid="calc-step-fx"
        title={t("ui.calculate.fx_block_title", "Exchange rates")}
        purpose={t("ui.calculate.fx_block_purpose", "Only needed when a file is in a different currency from a return")}>
      {/* v0.6 — onApply's inline "Save & re-run" button: only shown once
          a calc exists, else it just duplicated Run Calculation below. */}
      {periodValid ? (
        <FxRatesSection
          selectedRuns={selectedRuns}
          flaggedFxPairs={mergedFlaggedFxPairs}
          flaggedMonthlyFxPairs={mergedFlaggedMonthlyFxPairs}
          regime={regime}
          periodFxRates={periodFxRates}
          setPeriodFxRates={setPeriodFxRates}
          monthlyFxRates={monthlyFxRates}
          setMonthlyFxRates={setMonthlyFxRates}
          registrations={registrations}
          periodStart={periodStart}
          periodEnd={periodEnd}
          onApply={wf?.calc ? run : null}
          applyBusy={busy}
          previewLoading={fxPreviewLoading}
          mayBeIncomplete={mayBeIncomplete} previewAnswered={previewAnswered}
        />
      ) : (selectedRuns.length > 0 || flaggedFxPairs.length > 0) ? (
        <FxNote testid="fx-blocked-invalid-period" warn>
          {t("ui.calculate.fx_unavailable_invalid_period", { defaultValue: "Exchange-rate entry is unavailable until the filing period above is valid — the required currency-by-month grid is derived from the period start and end." })}
        </FxNote>
      ) : <FxNote testid="fx-section-waiting">{t("ui.calculate.fx_section.waiting", "Tick at least one file in part B. Any exchange rates the calculation needs will be asked for here.")}</FxNote>}
      </BlockCard>
      </div>

      {/* v0.4.39 T1 — async calc progress card, queue popup, and
          blocked/failed-outcome popup — see CalcJobSurfaces.jsx. */}
      <CalcJobSurfaces calcPoll={calcPoll} />
      <RunActionsConfirmDialog confirmState={confirmState} onDismiss={dismissConfirm} />

      <div className="border-t border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between">
        {error && (
          <div data-testid="calc-error" className="text-red-700 dark:text-red-300 text-sm">
            {typeof error === "string" ? error : JSON.stringify(error)}
          </div>
        )}
        <div className="flex-1" />
        <button
          type="button"
          data-testid="calc-run-button"
          aria-disabled={busy || selected.length === 0 || noRegistrations || fxMissing.length > 0 || fxInvalid.length > 0 || !periodValid}
          title={blockedReason || undefined}
          onClick={() => {
            const isBlocked = busy || selected.length === 0 || noRegistrations || fxMissing.length > 0 || fxInvalid.length > 0 || !periodValid;
            // v0.6.8 — this button used to be natively `disabled`, so
            // its ONLY explanation was a hover-only title= tooltip —
            // invisible on touch devices and easy to miss even with a
            // mouse, on the single most important button in the app.
            // Clicking it now (while visually still "disabled") shows
            // the blocked reason as an unmissable popup instead.
            if (isBlocked) { if (blockedReason) setShowBlockedDialog(true); return; }
            run();
          }}
          className={`${
            (busy || selected.length === 0 || noRegistrations || fxMissing.length > 0 || fxInvalid.length > 0 || !periodValid)
              ? "opacity-50 cursor-not-allowed bg-teal-600 text-white"
              : "bg-teal-600 hover:bg-teal-700 text-white shadow-lg shadow-teal-500/10"
          } font-semibold px-6 py-3 text-sm flex items-center gap-2 rounded-xl transition-colors`}
        >
          <Calculator size={16} weight="bold" />
          {busy ? t("ui.calculate.running") : t("ui.calculate.run_button")}
          <ArrowRight size={14} weight="bold" />
        </button>
      </div>
      <BlockingMessageDialog
        open={showBlockedDialog} testid="calc-run-blocked-dialog" severity="warning"
        title={t("ui.calculate.blocked_dialog_title", { defaultValue: "Can't run this calculation yet" })}
        onOpenChange={(o) => !o && setShowBlockedDialog(false)}
      ><p>{blockedReason}</p></BlockingMessageDialog>
    </section>
  );
};
